/**
 * Application Constants
 *
 * App-wide constants including API routes, enums, and configuration values.
 */

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * API base URL
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * API timeout (milliseconds)
 */
export const API_TIMEOUT = 30000; // 30 seconds

/**
 * Default request retry count
 */
export const DEFAULT_RETRY_COUNT = 3;

// ============================================================================
// CDN & APPLICATION LAUNCHER CONFIGURATION
// ============================================================================

/**
 * CDN base URL for tenant applications
 * This is where deployed applications are served from
 */
export const CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL || 'http://localhost:5174';

/**
 * Runtime API URL for tenant applications to call
 * This is the Data Plane API endpoint
 */
export const RUNTIME_API_URL = import.meta.env.VITE_RUNTIME_API_URL || 'http://localhost:3002/api/v1';

// ============================================================================
// AUTHENTICATION CONSTANTS
// ============================================================================

/**
 * Token storage keys
 */
export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const ACCESS_TOKEN_EXPIRY_KEY = 'access_token_expiry';
export const REFRESH_TOKEN_EXPIRY_KEY = 'refresh_token_expiry';

/**
 * Token expiration buffer (seconds before expiry to trigger refresh)
 * With 5-minute access tokens, refresh 1 minute before expiry
 */
export const TOKEN_EXPIRY_BUFFER = 60; // 1 minute

// ============================================================================
// PAGINATION CONSTANTS
// ============================================================================

/**
 * Default items per page
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Maximum items per page
 */
export const MAX_PAGE_SIZE = 100;

/**
 * Minimum items per page
 */
export const MIN_PAGE_SIZE = 1;

// ============================================================================
// FILE UPLOAD CONSTANTS
// ============================================================================

/**
 * Maximum file size for application code bundles (bytes)
 */
export const MAX_CODE_BUNDLE_SIZE = 52428800; // 50MB

/**
 * Maximum file size for volume uploads (bytes)
 */
export const MAX_VOLUME_FILE_SIZE = 5368709120; // 5GB

/**
 * Allowed file types for code bundles
 */
export const ALLOWED_CODE_BUNDLE_TYPES = ['.zip'];

/**
 * Common MIME types
 */
export const MIME_TYPES = {
  // Archives
  ZIP: 'application/zip',
  TAR: 'application/x-tar',
  GZIP: 'application/gzip',

  // Images
  JPEG: 'image/jpeg',
  PNG: 'image/png',
  GIF: 'image/gif',
  SVG: 'image/svg+xml',
  WEBP: 'image/webp',

  // Documents
  PDF: 'application/pdf',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

  // Text
  TEXT: 'text/plain',
  HTML: 'text/html',
  CSS: 'text/css',
  JAVASCRIPT: 'text/javascript',
  JSON: 'application/json',
  XML: 'application/xml',

  // Media
  MP4: 'video/mp4',
  WEBM: 'video/webm',
  MP3: 'audio/mpeg',
  WAV: 'audio/wav',
} as const;

// ============================================================================
// STATUS CONSTANTS
// ============================================================================

/**
 * User status values
 */
export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

/**
 * Tenant status values
 */
export const TENANT_STATUS = {
  PENDING_VERIFICATION: 'pending_verification',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
} as const;

/**
 * Invitation status values
 */
export const INVITATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
} as const;

/**
 * Application deployment status values
 */
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  DEPLOYING: 'deploying',
  DEPLOYED: 'deployed',
  FAILED: 'failed',
} as const;

// ============================================================================
// STATUS BADGE CONFIG MAPS (for use with Badge component)
// ============================================================================

/**
 * Application deployment status → Badge variant mapping
 */
export const applicationStatusConfig: Record<string, { label: string; variant: string }> = {
  DEPLOYED: { label: 'Deployed', variant: 'success' },
  PENDING: { label: 'Pending', variant: 'warning' },
  DEPLOYING: { label: 'Deploying', variant: 'info' },
  FAILED: { label: 'Failed', variant: 'danger' },
};

/**
 * User status → Badge variant mapping
 */
export const userStatusConfig: Record<string, { label: string; variant: string }> = {
  active: { label: 'Active', variant: 'success' },
  inactive: { label: 'Inactive', variant: 'neutral' },
  suspended: { label: 'Suspended', variant: 'danger' },
};

/**
 * Invitation status → Badge variant mapping
 */
export const invitationStatusConfig: Record<string, { label: string; variant: string }> = {
  pending: { label: 'Pending', variant: 'warning' },
  accepted: { label: 'Accepted', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
};

/**
 * Database connection engine → display label mapping
 */
export const dbEngineConfig: Record<string, { label: string; variant: string }> = {
  POSTGRESQL: { label: 'PostgreSQL', variant: 'info' },
  SQL_SERVER: { label: 'SQL Server', variant: 'primary' },
  MYSQL: { label: 'MySQL', variant: 'warning' },
};

/**
 * Database connection type → display label mapping
 */
export const dbConnectionTypeConfig: Record<string, { label: string; variant: string }> = {
  SELF_MANAGED: { label: 'Self-Managed', variant: 'neutral' },
  KETRICS_MANAGED: { label: 'Ketrics Managed', variant: 'primary' },
};

/**
 * Managed SQL DB status → Badge variant mapping
 */
export const managedSqlDbStatusConfig: Record<string, { label: string; variant: string }> = {
  available: { label: 'Available', variant: 'success' },
  creating: { label: 'Creating', variant: 'info' },
  deleting: { label: 'Deleting', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
};

// ============================================================================
// VOLUME PERMISSIONS
// ============================================================================

/**
 * Volume permission values
 */
export const VOLUME_PERMISSIONS = {
  GET_OBJECT: 'GetObject',
  PUT_OBJECT: 'PutObject',
  DELETE_OBJECT: 'DeleteObject',
  LIST_OBJECTS: 'ListObjects',
} as const;

/**
 * Volume permission labels (human-readable)
 */
export const VOLUME_PERMISSION_LABELS: Record<string, string> = {
  GetObject: 'Read',
  PutObject: 'Write',
  DeleteObject: 'Delete',
  ListObjects: 'List',
};

// ============================================================================
// UI CONSTANTS
// ============================================================================

/**
 * Toast notification duration (milliseconds)
 */
export const TOAST_DURATION = 5000; // 5 seconds

/**
 * Debounce delay for search inputs (milliseconds)
 */
export const SEARCH_DEBOUNCE_DELAY = 300; // 300ms

/**
 * Auto-refresh interval for data (milliseconds)
 */
export const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

/**
 * Loading spinner delay (milliseconds)
 * Prevents flash of loading state for fast requests
 */
export const LOADING_DELAY = 200; // 200ms

// ============================================================================
// DATE FORMATS
// ============================================================================

/**
 * Date format strings (date-fns)
 */
export const DATE_FORMATS = {
  SHORT: 'MMM dd, yyyy',
  LONG: 'MMMM dd, yyyy',
  WITH_TIME: 'MMM dd, yyyy HH:mm',
  FULL: 'MMMM dd, yyyy HH:mm:ss',
  TIME_ONLY: 'HH:mm:ss',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
} as const;

// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================

/**
 * Password requirements
 */
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 128,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBER: true,
  REQUIRE_SPECIAL: true,
};

/**
 * Tenant code requirements
 */
export const TENANT_CODE_REQUIREMENTS = {
  MIN_LENGTH: 3,
  MAX_LENGTH: 50,
  PATTERN: /^[a-z0-9_-]+$/,
};

/**
 * Name requirements
 */
export const NAME_REQUIREMENTS = {
  MIN_LENGTH: 1,
  MAX_LENGTH: 100,
};

/**
 * Email requirements
 */
export const EMAIL_REQUIREMENTS = {
  MAX_LENGTH: 255,
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Application route paths
 */
export const ROUTES = {
  // Public routes
  LOGIN: '/login',
  REGISTER: '/register',
  VERIFY_EMAIL: '/verify-email',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',

  // Protected routes
  DASHBOARD: '/app/dashboard',
  USERS: '/app/users',
  APPLICATIONS: '/app/applications',
  VOLUMES: '/app/volumes',
  LAUNCHER: '/app/launcher',
  LAUNCH: '/app/launch', // Launch specific application by ID
  SETTINGS: '/app/settings',

  // Other
  UNAUTHORIZED: '/unauthorized',
  NOT_FOUND: '/404',
} as const;

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  // Token keys
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  ACCESS_TOKEN_EXPIRY: 'access_token_expiry',
  REFRESH_TOKEN_EXPIRY: 'refresh_token_expiry',
  // User/tenant data
  USER_DATA: 'user_data',
  TENANT_DATA: 'tenant_data',
  // UI preferences
  THEME: 'theme',
  LAST_TENANT_CODE: 'last_tenant_code',
} as const;

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/**
 * Feature flags
 */
export const FEATURE_FLAGS = {
  ENABLE_GROUPS: import.meta.env.VITE_ENABLE_GROUPS === 'true',
  ENABLE_ROLES: import.meta.env.VITE_ENABLE_ROLES === 'true',
  ENABLE_DARK_MODE: import.meta.env.VITE_ENABLE_DARK_MODE === 'true',
  ENABLE_DEVTOOLS: import.meta.env.VITE_ENABLE_DEVTOOLS === 'true',
} as const;

// ============================================================================
// ERROR MESSAGES
// ============================================================================

/**
 * Common error messages
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error: Unable to connect to the server. Please check your internet connection.',
  UNAUTHORIZED: 'Unauthorized: Please log in to continue.',
  FORBIDDEN: 'Forbidden: You do not have permission to perform this action.',
  NOT_FOUND: 'Not found: The requested resource could not be found.',
  SERVER_ERROR: 'Server error: Something went wrong on our end. Please try again later.',
  VALIDATION_ERROR: 'Validation error: Please check your input and try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

// ============================================================================
// SUCCESS MESSAGES
// ============================================================================

/**
 * Common success messages
 */
export const SUCCESS_MESSAGES = {
  LOGIN: 'Successfully logged in.',
  LOGOUT: 'Successfully logged out.',
  REGISTRATION: 'Registration successful. Please check your email to verify your account.',
  VERIFICATION: 'Email verified successfully.',
  PASSWORD_RESET_REQUEST: 'Password reset email sent. Please check your inbox.',
  PASSWORD_RESET: 'Password reset successful. You can now log in with your new password.',
  USER_INVITED: 'User invited successfully.',
  USER_UPDATED: 'User updated successfully.',
  USER_DELETED: 'User deleted successfully.',
  APPLICATION_CREATED: 'Application created successfully.',
  APPLICATION_UPDATED: 'Application updated successfully.',
  APPLICATION_DELETED: 'Application deleted successfully.',
  VOLUME_CREATED: 'Volume created successfully.',
  VOLUME_UPDATED: 'Volume updated successfully.',
  VOLUME_DELETED: 'Volume deleted successfully.',
} as const;
